import { defineStore } from 'pinia';
import Fuse from 'fuse.js';
import { useAuthStore } from '@/stores/auth';
import { useBotsStore } from '@/stores/bots';
import { useIntentsStore } from '@/stores/intents';

/** A search result / recent-query item shown in the global search */
interface SearchItem {
  /** Label rendered in the result list */
  displayName: string;
  /** Result kind: 'bot' | 'intent' */
  type: string;
  /** Semantic icon key ('bot' | 'intent' | 'suggestion'); SearchComponent.vue maps it to a Lucide component */
  icon: string;
  /** The underlying bot or intent object */
  value: any;
}

interface SearchState {
  /** Bot search results (max 5) */
  botResults: SearchItem[];
  /** Intent search results (max 5) */
  intentResults: SearchItem[];
  /** Recent picked queries, newest first (max 100 kept, 5 shown) */
  searchQueries: SearchItem[];
  /** Unused suggestion results placeholder */
  suggestionResults: any[];
  /**
   * FuseJS search instance for bots (unused; the `botIndex` getter builds its own).
   * Renamed from legacy state prop `botIndex` — it collided with the `botIndex` getter, which is
   * NOT an identity passthrough (it builds a fresh `Fuse` instance) and is the consumer-facing
   * name (`store.getters['search/botIndex']` in `SearchComponent.vue`), so the getter keeps the
   * name and this dead/unused state field is renamed instead. Kept around as intentionally-dead
   * code, exactly as in the legacy module.
   */
  botIndexState: any;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/search.ts` (namespace `search`).
 * Persisted to `localStorage` (full state) — matches the legacy `vuexLocal` reducer's
 * `search: state.search` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had both a state prop and a getter named `botResults`/`intentResults`, where the getter
 *   is a pure identity passthrough of the state prop. Per the state-prop/getter collision rule,
 *   the getters are renamed `botResultsGetter` / `intentResultsGetter`; the state props keep their
 *   original names (readable directly as `useSearchStore().botResults`).
 * - Legacy had both a state prop and a getter named `botIndex`, but the getter is NOT an identity
 *   passthrough (it lazily builds a `Fuse` index from `rootState.bots.bots`) and is the
 *   consumer-facing name used by `SearchComponent.vue`. The getter keeps the name `botIndex`; the
 *   dead/unused state prop is renamed `botIndexState` instead (see field doc above).
 * - `removeQuery` existed as BOTH an action (which just committed the mutation) and a mutation of
 *   the same name — merged into a single action.
 * - `setResults`, `unshiftResult`, `addQuery` existed only as mutations (no matching actions) —
 *   they become same-named actions.
 * - Cross-namespace reads (`rootState.bots.*`, `rootGetters['auth/*']`, `rootGetters['intents/*']`)
 *   are resolved via `useBotsStore()` / `useAuthStore()` / `useIntentsStore()` called inside the
 *   getter/action body.
 */
export const useSearchStore = defineStore('search', {
  state: (): SearchState => ({
    botResults: [],
    intentResults: [],
    searchQueries: [],
    suggestionResults: [],
    //searching: false
    botIndexState: null, // FuseJS search instance for bots (dead/unused; see field doc)
  }),
  getters: {
    /**
     * Renamed from legacy getter `botResults` (collided with the state prop of the same name;
     * this getter is a pure identity passthrough, so it gets the `Getter` suffix).
     */
    botResultsGetter(state): SearchItem[] {
      return state.botResults;
    },
    /**
     * Renamed from legacy getter `intentResults` (collided with the state prop of the same name;
     * this getter is a pure identity passthrough, so it gets the `Getter` suffix).
     */
    intentResultsGetter(state): SearchItem[] {
      return state.intentResults;
    },
    queries(): SearchItem[] {
      const botsStore = useBotsStore();
      const authStore = useAuthStore();

      const currentBotId = botsStore.currentBot?.uniqueBotId;
      if (!currentBotId) return [];

      return this.searchQueries
        .filter((query) => {
          if (query.type === 'bot' && !authStore.isAdmin) {
            return false;
          }

          return query.type !== 'intent' || query.value.botId === currentBotId;
        })
        .slice(0, 5);
    },
    botIndex(): Fuse<any> {
      const botsStore = useBotsStore();

      return new Fuse(botsStore.bots, {
        keys: [
          // If you use Bot ID for matching, you likely want that result higher
          {
            name: 'uniqueBotId',
            weight: 0.8,
          },
          {
            name: 'name',
            weight: 0.6,
          },
        ],
        threshold: 0.4,
        minMatchCharLength: 2,
        distance: 60,
      });
    },
    intentIndex(): Fuse<any> | null {
      const intentsStore = useIntentsStore();
      const authStore = useAuthStore();

      // Exit if not initialized yet
      if (!intentsStore.intents) return null;

      const language = authStore.user.language;
      const formattedIntents = (intentsStore.intents as any[]).map((intent: any) => ({
        displayName: intentsStore.displayName(intent.intent, language) || intent.displayName,
        type: 'intent',
        // Semantic icon key; SearchComponent.vue maps it to a Lucide component.
        icon: intent.suggested ? 'suggestion' : 'intent',
        value: intent,
      }));
      intentsStore.suggested.forEach((s: any) => {
        formattedIntents.push({
          displayName: intentsStore.displayName(s.intent, language) || s.displayName,
          type: 'intent',
          icon: 'suggestion',
          value: s,
        });
      });

      const s = new Fuse(formattedIntents, {
        keys: [
          {
            name: 'displayName',
            weight: 0.8,
          },
          {
            name: 'intent',
            weight: 0.3,
          },
        ],
        threshold: 0.6,
        minMatchCharLength: 1,
      });

      (window as any).fuse = s;
      return s;
    },
  },
  actions: {
    async search(payload: { text: string }) {
      const { text } = payload;
      if (!text || text.trim().length === 0 || text === '') {
        return this.setResults({ bots: [], intents: [] });
      }
      //this.searching = true; // dead code kept from legacy

      // search bots
      let bots: SearchItem[] = [];
      const authStore = useAuthStore();
      if (authStore.isAdmin) {
        bots = this.botIndex.search(text).map(({ item }: any) => {
          return {
            displayName: item.name,
            type: 'bot',
            icon: 'bot',
            value: item,
          };
        });
      }

      // search intent names
      const intents = (this.intentIndex as any).search(text).map(({ item }: any) => item);

      this.setResults({ bots, intents });
    },
    async pick(item: SearchItem) {
      this.addQuery(item);
      this.setResults({ bots: [], intents: [] });
    },
    /**
     * Merged from legacy action `removeQuery` (which only committed the mutation of the same
     * name) + legacy mutation `removeQuery`.
     */
    async removeQuery(query: SearchItem) {
      this.searchQueries = this.searchQueries.filter((q) => {
        return !(q.type === query.type && q.displayName === query.displayName);
      });
    },
    /**
     * Converted from legacy mutation `setResults` (no matching legacy action existed).
     */
    setResults(payload: { bots: SearchItem[]; intents: SearchItem[] }) {
      this.botResults = payload.bots.slice(0, 5);

      this.intentResults = payload.intents.slice(0, 5);
    },
    /**
     * Converted from legacy mutation `unshiftResult` (no matching legacy action existed).
     */
    unshiftResult(payload: { bot?: SearchItem; intent?: SearchItem }) {
      const { bot, intent } = payload;

      if (intent) {
        this.intentResults = this.intentResults.filter((q) => {
          return !(q.type === intent.type && q.displayName === intent.displayName);
        });

        this.intentResults.unshift(intent);
        if (this.intentResults.length > 5) {
          this.intentResults.length = 5;
        }
      }

      if (bot) {
        // NOTE: unshift returns a number; this assignment is a pre-existing bug kept for behavior parity
        this.botResults = (this.botResults as any).unshift(bot);
        if (this.botResults.length > 5) {
          this.botResults.length = 5;
        }
      }
    },
    /**
     * Converted from legacy mutation `addQuery` (no matching legacy action existed).
     */
    addQuery(query: SearchItem) {
      this.searchQueries = this.searchQueries.filter((q) => {
        return !(q.type === query.type && q.displayName === query.displayName);
      });

      this.searchQueries.unshift(query);
      if (this.searchQueries.length > 100) {
        this.searchQueries.length = 100;
      }
    },
  },
  vuexPersist: {
    local: true,
  },
});
